import {dissoc, insert, partition, prop, propEq, reduce, remove, whereEq} from 'ramda';
import {collectAs, define, elems, find, modify, select, when, values} from 'partial.lenses';

const renumberTeasers = teaserList => teaserList.map((teaser, i) => ({...teaser, position: i + 1}));

export function dragTeaser(teaserLists, oldLocation, newLocation, drop = false) {
    let movedTeaser = teaserLists[oldLocation.groupIndex].teasers[oldLocation.teaserIndex];
    if (!movedTeaser.lastValidLocation) {
        movedTeaser = {...movedTeaser, lastValidLocation: oldLocation};
    }
    if (!newLocation) {
        newLocation = movedTeaser.lastValidLocation;
    }
    if (drop) {
        movedTeaser = {
            ...movedTeaser,
            modifiedPosition:
                movedTeaser.modifiedPosition || movedTeaser.lastValidLocation.teaserIndex !== newLocation.teaserIndex,
            lastValidLocation: null,
        };
    }
    return teaserLists.map((list, listIndex) => {
        let newTeasers = list.teasers;
        if (listIndex === oldLocation.groupIndex) {
            newTeasers = remove(oldLocation.teaserIndex, 1, newTeasers);
        }
        if (listIndex === newLocation.groupIndex) {
            newTeasers = insert(newLocation.teaserIndex, movedTeaser, newTeasers);
        }
        if (newTeasers === list.teasers) {
            return list;
        }
        return {...list, teasers: renumberTeasers(newTeasers)};
    });
}

const removeTeaserFromList = ({sendingListUuid, uuid}, teaserlists) => {
    let removedTeaser;

    const filtered = teaserlists.map(teaserList => {
        if (teaserList.uuid !== sendingListUuid) {
            return teaserList;
        }
        const [[removed], teasers] = partition(propEq('uuid', uuid), teaserList.teasers);
        removedTeaser = removed;
        return {
            ...teaserList,
            teasers: renumberTeasers(teasers),
        };
    });

    return {
        teaserLists: filtered,
        removedTeaser,
    };
};

const addTeaserToList = ({listUuid, prevUuid, position}, teaserLists, movedTeaser) =>
    teaserLists.map(teaserList => {
        if (teaserList.uuid !== listUuid) {
            return teaserList;
        }

        let insertAt;
        if (prevUuid) {
            // Find the index of the previous teaser in the sortable list (unfound will be 0)
            insertAt = teaserList.teasers.findIndex(propEq('uuid', prevUuid)) + 1;

            // If not there (prev teaser has gone away), just place according to target position
            if (!insertAt) {
                insertAt = Math.min(position - 1, teaserList.teasers.length);
            }
        } else {
            // Falsy prevUuid indicates teaser should be at top
            insertAt = 0;
        }

        let teasers;
        if (insertAt === teaserList.teasers.length) {
            teasers = teaserList.teasers.concat(movedTeaser);
        } else {
            teasers = insert(insertAt, {...movedTeaser, modifiedPosition: true}, teaserList.teasers);
        }
        return {...teaserList, teasers: renumberTeasers(teasers)};
    });

// change: {
//     listUuid : "84f78e37-e97d-41e4-94fc-4d1793e3c38e",
//     position : 2
//     prevUuid : "1dd221ac-1936-4635-b61e-8ee6d8611652",
//     sendingListUuid : undefined,
//     type : "move",
//     uuid : "53025dc6-efa3-4311-8563-0b604f23ac2b"
// }
const editors = {
    add: (data, teaserlists) =>
        teaserlists.map(teaserList => {
            if (teaserList.uuid !== data.listUuid) {
                return teaserList;
            }
            const teasers = [{...data.attrs, uuid: data.uuid, newTeaser: true}, ...teaserList.teasers];
            return {
                ...teaserList,
                teasers: renumberTeasers(teasers),
            };
        }),
    edit: (data, teaserlists) =>
        teaserlists.map(teaserList => {
            let hasChanges = false;
            const teasers = teaserList.teasers.map(teaser => {
                if (teaser.uuid !== data.uuid) {
                    return teaser;
                }
                hasChanges = true;
                return {
                    ...teaser,
                    ...data.attrs,
                    modifiedData: true,
                };
            });
            return hasChanges ? {...teaserList, teasers} : teaserList;
        }),
    move(change, teaserlists) {
        // Remove the moved teaser from it's original list
        const {teaserLists: preAddTeaserlists, removedTeaser} = removeTeaserFromList(change, teaserlists);

        // If teaser is missing from target list something has gone very wrong with our algorithm
        if (!removedTeaser) {
            console.error('Failed to reapply teaser move. Could not find teaser in source list', change);
            return teaserlists;
        }

        const withAddTeaserlists = addTeaserToList(change, preAddTeaserlists, removedTeaser);

        return withAddTeaserlists;
    },
};

export const buildEditor = data => teaserLists => editors[data.type](data, teaserLists);

function recordPotentialMismatch(teaserLists, data) {
    if (data.type !== 'move') {
        return null;
    }
    const teaserList = teaserLists.find(propEq('uuid', data.listUuid));
    const teaser = teaserList.teasers.find(propEq('uuid', data.uuid));
    if (!teaser) {
        return null;
    }
    return {
        teaserList: teaserList.title,
        position: teaser.position,
        intendedPosition: data.position,
        title: teaser.title,
        uuid: teaser.uuid,
    };
}

const removeUuid = dissoc('uuid');

const selectTeaserByUuid = (teaserLists, uuid) =>
    select(['teasers', define([]), elems, find(whereEq({uuid}))], teaserLists);

const buildEditedList = (teaserLists, mismatchesMap) =>
    reduce((baseLists, edit) => {
        const edited = buildEditor(edit)(baseLists);

        const mm = recordPotentialMismatch(edited, edit);
        if (mm) {
            // Only last conflict is saved for any particular teaser
            mismatchesMap[mm.uuid] = mm;
        }

        return edited;
    }, teaserLists);

const getMismatches = teaserLists =>
    collectAs(
        mm => {
            const teaser = selectTeaserByUuid(teaserLists, mm.uuid);
            if (!teaser || mm.title === teaser.title) {
                return mm;
            }
            return {...mm, title: teaser.title};
        },
        [values, when(mm => mm.position !== mm.intendedPosition)],
    );

const filterNewTeasers = modify([elems, 'teasers', elems, when(prop('newTeaser'))], removeUuid);

export function applyEdits(teaserLists, edits) {
    const mismatchesMap = {};
    const newTeaserLists = buildEditedList(teaserLists, mismatchesMap)(edits);

    const mismatches = getMismatches(newTeaserLists)(Object.values(mismatchesMap));

    return {
        teaserLists: filterNewTeasers(newTeaserLists),
        mismatches,
    };
}
